import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrder, cancelOrder, PortalOrder } from '../../services/portalApi';
import { StatusBadge } from './PortalShared';

const PortalOrderDetailPage: React.FC = () => {
  const { orgAlias, orderId } = useParams<{ orgAlias: string; orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState<PortalOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getOrder(Number(orderId)).then(setOrder).finally(() => setLoading(false));
  }, [orderId]);

  const handleCancel = async () => {
    if (!window.confirm(t('portal.orderDetail.cancelConfirm'))) return;
    setCancelling(true);
    try {
      const updated = await cancelOrder(Number(orderId));
      setOrder(updated);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-gray-400">{t('portal.orderDetail.loading')}</div>
  );

  if (!order) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-gray-400">{t('portal.orderDetail.notFound')}</div>
  );

  const canCancel = ['draft', 'submitted'].includes(order.status);
  const items: any[] = order.parts_in_order?.items ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <Link to={`/portal/${orgAlias}/orders`} className="text-sm text-blue-600 hover:underline">{t('portal.orderDetail.backToOrders')}</Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-gray-700">{order.name || `Order #${order.id}`}</span>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{order.name || `Order #${order.id}`}</h1>
            <p className="text-sm text-gray-500 mt-1 font-mono">{order.transaction_id}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={order.status} />
            {canCancel && (
              <button onClick={handleCancel} disabled={cancelling}
                className="text-sm text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {cancelling ? t('portal.orderDetail.cancelling') : t('portal.orderDetail.cancelOrder')}
              </button>
            )}
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            [t('portal.orderDetail.colVin'), order.vin || '—'],
            [t('portal.orderDetail.colServiceType'), order.service_type?.replace(/_/g, ' ')],
            [t('portal.orderDetail.colCreated'), new Date(order.created_at).toLocaleDateString()],
            [t('portal.orderDetail.colUpdated'), new Date(order.updated_at).toLocaleDateString()],
          ].map(([label, value]) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{label}</p>
              <p className="font-semibold text-gray-800 capitalize">{value}</p>
            </div>
          ))}
        </div>

        {/* Parts */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">{t('portal.orderDetail.partsTitle')}</h2>
          </div>
          {items.length === 0 ? (
            <p className="px-5 py-8 text-center text-gray-400 text-sm">{t('portal.orderDetail.noParts')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[t('portal.orderDetail.colPart'), t('portal.orderDetail.colPartNo'), t('portal.orderDetail.colQty'), t('portal.orderDetail.colPrice'), t('portal.orderDetail.colStock'), t('portal.orderDetail.colSupplier')].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item: any, i: number) => {
                  const pd = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
                  const description =
                    item.description || pd.description || pd.DESCRIPTION ||
                    item.name || item.part_name || item.related_corrected_name ||
                    pd.part_name || '—';
                  const partNo =
                    item.oeNumber || item.oe_number ||
                    pd.part_number || pd.PART_ID || pd.part_id ||
                    item.part_number || item.partNumber || '—';
                  const qty = item.qty ?? item.quantity ?? pd.quantity ?? 1;
                  const supplier = item.supplierId || item.supplier_id || pd.supplier_id || '—';
                  const rawPrice = item.price ?? item.unitPrice ?? pd.price ?? pd.unit_price ?? null;
                  const price = rawPrice != null ? `$${Number(rawPrice).toFixed(2)}` : null;
                  const stock = item.stock ?? item.inventory ?? pd.stock ?? pd.inventory ?? pd.qty ?? null;
                  return (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{description}</td>
                      <td className="px-4 py-3 font-mono text-gray-600 text-xs">{partNo}</td>
                      <td className="px-4 py-3 text-gray-600">{qty}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {price ?? <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {stock != null ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${Number(stock) > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                            {Number(stock) > 0 ? '✓' : '✗'} {stock}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{supplier}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Status timeline */}
        {order.status_notes && order.status_notes.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">{t('portal.orderDetail.historyTitle')}</h2>
            <ol className="relative border-l border-gray-200 ml-3 space-y-4">
              {order.status_notes.map((note: any, i: number) => (
                <li key={i} className="ml-6">
                  <span className="absolute -left-3 w-5 h-5 rounded-full bg-blue-100 border-2 border-blue-400 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  </span>
                  <p className="text-sm font-medium text-gray-800">{note.note}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{note.actor && <span className="mr-2">{note.actor}</span>}{new Date(note.ts).toLocaleString()}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalOrderDetailPage;
